import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Target, Plus, Search, Filter, Download, Lock, TrendingUp, TrendingDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Progress } from "@/components/ui/progress";
import { endpoints } from "@/lib/api";
import ObjectiveModal from "@/components/objectives/ObjectiveModal";

const STATUS_TONE = {
  "Not Started": "bg-slate-100 text-slate-600 border-slate-200",
  "On Track": "bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-500/15 dark:text-emerald-300 dark:border-emerald-500/25",
  "At Risk": "bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-500/15 dark:text-amber-300 dark:border-amber-500/25",
  "Off Track": "bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-500/15 dark:text-rose-300 dark:border-rose-500/25",
  "Met": "bg-indigo-50 text-indigo-700 border-indigo-200 dark:bg-indigo-500/15 dark:text-indigo-300 dark:border-indigo-500/25",
};

const attainment = (o) => {
  if (o.current_value == null || !o.target_value) return null;
  if (o.direction === "up") {
    return Math.min(100, Math.round((o.current_value / o.target_value) * 100));
  }
  return Math.min(100, Math.round((o.target_value / Math.max(o.current_value, 0.0001)) * 100));
};

export default function ObjectivesPage() {
  const [params, setParams] = useSearchParams();
  const [objectives, setObjectives] = useState([]);
  const [clients, setClients] = useState([]);
  const [authorized, setAuthorized] = useState(null);
  const [years, setYears] = useState([new Date().getFullYear()]);
  const [year, setYear] = useState(Number(params.get("year")) || new Date().getFullYear());
  const [clientSlug, setClientSlug] = useState(params.get("client") || "all");
  const [status, setStatus] = useState("all");
  const [q, setQ] = useState("");
  const [modal, setModal] = useState({ open: false, id: null });

  const load = () => {
    const p = { year };
    if (clientSlug !== "all") p.client_slug = clientSlug;
    return endpoints.listObjectives(p).then(setObjectives).catch(() => {});
  };

  useEffect(() => {
    endpoints.me().then((u) => {
      endpoints.listConsultants().then((cs) => {
        const me = cs.find((c) => c.name === u.name);
        setAuthorized(u.canonical_role === "admin" || u.canonical_role === "staff" || !!me?.is_manager);
      });
    });
    endpoints.listClients().then(setClients).catch(() => {});
    endpoints.objectiveYears().then((ys) => { if (ys?.length) setYears(ys); }).catch(() => {});
  }, []);

  useEffect(() => {
    if (authorized) load();
    const next = new URLSearchParams(params);
    next.set("year", String(year));
    if (clientSlug === "all") next.delete("client"); else next.set("client", clientSlug);
    setParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authorized, year, clientSlug]);

  const filtered = useMemo(() => objectives.filter((o) => {
    if (status !== "all" && o.status !== status) return false;
    if (q && !`${o.objective} ${o.kpi} ${o.responsible_person} ${o.client_name}`.toLowerCase().includes(q.toLowerCase())) return false;
    return true;
  }), [objectives, q, status]);

  const stats = useMemo(() => {
    const s = { total: objectives.length, met: 0, on_track: 0, at_risk: 0, off_track: 0 };
    objectives.forEach((o) => {
      if (o.status === "Met") s.met++;
      else if (o.status === "On Track") s.on_track++;
      else if (o.status === "At Risk") s.at_risk++;
      else if (o.status === "Off Track") s.off_track++;
    });
    return s;
  }, [objectives]);

  if (authorized === null) return <div className="p-8 text-sm text-slate-500">Loading...</div>;
  if (!authorized) {
    return (
      <div className="p-6 lg:p-10 max-w-3xl mx-auto">
        <div className="glass-card rounded-3xl p-10 text-center">
          <div className="mx-auto h-12 w-12 rounded-2xl bg-rose-50 text-rose-600 flex items-center justify-center mb-4"><Lock className="h-5 w-5" /></div>
          <h1 className="font-display text-xl font-extrabold mb-1">Authorized staff only</h1>
          <p className="text-sm text-slate-500">Strategic Objectives are restricted to ISOQuest staff.</p>
        </div>
      </div>
    );
  }

  const csvHref = endpoints.exportObjectivesCsvUrl({
    year,
    ...(clientSlug !== "all" ? { client_slug: clientSlug } : {}),
  });

  const StatCard = ({ label, value, tone, testid, onClick, active }) => (
    <button
      data-testid={testid}
      onClick={onClick}
      className={`text-left rounded-2xl border p-4 bg-white/60 dark:bg-slate-900/40 backdrop-blur-md card-hover transition-all ${active ? "ring-2 ring-indigo-500/50" : ""} border-slate-200/70 dark:border-white/10`}
    >
      <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">{label}</div>
      <div className={`text-2xl font-extrabold font-display mt-1 ${tone}`}>{value}</div>
    </button>
  );

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-[1600px] mx-auto">
      {/* Header */}
      <div className="glass-card rounded-3xl p-4 sm:p-5 mb-5 flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-2xl bg-gradient-to-br from-indigo-500 to-blue-600 flex items-center justify-center shadow-md shadow-indigo-500/25">
            <Target className="h-5 w-5 text-white" />
          </div>
          <div>
            <h1 className="font-display text-lg sm:text-xl font-extrabold tracking-tight">Strategic Objectives</h1>
            <p className="text-xs text-slate-500">{filtered.length} of {objectives.length} objectives for {year}</p>
          </div>
        </div>
        <div className="ml-auto flex flex-wrap gap-2 items-center">
          <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
            <SelectTrigger data-testid="obj-year-select" className="h-9 w-[110px] text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>{years.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
          </Select>
          <Button asChild variant="outline" size="sm" data-testid="obj-export-csv-button">
            <a href={csvHref} download><Download className="h-3.5 w-3.5 mr-1" /> Export CSV</a>
          </Button>
          <Button data-testid="obj-new-button" className="brand-gradient text-white" size="sm" onClick={() => setModal({ open: true, id: null })}>
            <Plus className="h-4 w-4 mr-1" /> New objective
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-5">
        <StatCard label="Total" value={stats.total} tone="text-indigo-600" testid="obj-stat-total" onClick={() => setStatus("all")} active={status === "all"} />
        <StatCard label="Met" value={stats.met} tone="text-indigo-600" testid="obj-stat-met" onClick={() => setStatus("Met")} active={status === "Met"} />
        <StatCard label="On track" value={stats.on_track} tone="text-emerald-600" testid="obj-stat-on-track" onClick={() => setStatus("On Track")} active={status === "On Track"} />
        <StatCard label="At risk" value={stats.at_risk} tone="text-amber-600" testid="obj-stat-at-risk" onClick={() => setStatus("At Risk")} active={status === "At Risk"} />
        <StatCard label="Off track" value={stats.off_track} tone="text-rose-600" testid="obj-stat-off-track" onClick={() => setStatus("Off Track")} active={status === "Off Track"} />
      </div>

      {/* Filters */}
      <div className="glass-card rounded-3xl p-4 mb-5 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input data-testid="obj-search-input" placeholder="Search objective, KPI, responsible..." value={q} onChange={(e) => setQ(e.target.value)} className="pl-9 h-9" />
        </div>
        <Select value={clientSlug} onValueChange={setClientSlug}>
          <SelectTrigger data-testid="obj-client-filter" className="h-9 w-[220px] text-xs"><Filter className="h-3.5 w-3.5 mr-1 text-slate-400" /><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All clients</SelectItem>
            {clients.map((c) => <SelectItem key={c.slug} value={c.slug}>{c.company_name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger data-testid="obj-status-filter" className="h-9 w-[160px] text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="Met">Met</SelectItem>
            <SelectItem value="On Track">On Track</SelectItem>
            <SelectItem value="At Risk">At Risk</SelectItem>
            <SelectItem value="Off Track">Off Track</SelectItem>
            <SelectItem value="Not Started">Not Started</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Register - card grid */}
      {filtered.length === 0 ? (
        <div className="glass-card rounded-3xl p-10 text-center text-sm text-slate-500">No objectives yet for {year}. Add your first one to get started.</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {filtered.map((o) => {
            const pct = attainment(o);
            const DirIcon = o.direction === "up" ? TrendingUp : TrendingDown;
            return (
              <button
                key={o.id}
                data-testid={`obj-card-${o.id}`}
                onClick={() => setModal({ open: true, id: o.id })}
                className="glass-card rounded-3xl p-5 text-left card-hover group"
              >
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="min-w-0">
                    <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">{o.client_name}</div>
                    <h3 className="font-semibold text-sm mt-0.5 line-clamp-2">{o.objective}</h3>
                  </div>
                  <Badge variant="outline" className={`text-[10px] rounded-full shrink-0 ${STATUS_TONE[o.status] || ""}`}>{o.status}</Badge>
                </div>
                <div className="text-[11px] text-slate-500 mb-3 flex items-center gap-1.5">
                  <DirIcon className="h-3 w-3" /> {o.kpi || "—"} · {o.frequency}
                </div>
                <div className="flex items-end justify-between mb-2">
                  <div>
                    <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Current</div>
                    <div className="text-xl font-extrabold font-display font-mono">
                      {o.current_value ?? "—"}
                      {o.unit && o.current_value != null && <span className="text-xs font-semibold text-slate-500 ml-0.5">{o.unit}</span>}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Target</div>
                    <div className="text-sm font-mono font-semibold text-slate-600 dark:text-slate-300">
                      {o.target_value}{o.unit ? ` ${o.unit}` : ""}
                    </div>
                  </div>
                </div>
                {pct != null && (
                  <div>
                    <Progress value={pct} className="h-1.5" />
                    <div className="flex items-center justify-between mt-1 text-[10px] text-slate-500">
                      <span>{pct}% of target</span>
                      <span>{o.latest_period || "—"}</span>
                    </div>
                  </div>
                )}
                <div className="mt-3 flex items-center justify-between text-[11px] text-slate-500">
                  <span>{o.responsible_person || "Unassigned"}</span>
                  <span className="opacity-60 group-hover:opacity-100 text-indigo-600 font-semibold">Open →</span>
                </div>
              </button>
            );
          })}
        </div>
      )}

      <ObjectiveModal
        open={modal.open}
        objectiveId={modal.id}
        defaultClientSlug={clientSlug !== "all" ? clientSlug : null}
        defaultYear={year}
        clients={clients}
        onOpenChange={(o) => { if (!o) setModal({ open: false, id: null }); }}
        onChanged={() => { load(); endpoints.objectiveYears().then((ys) => ys?.length && setYears(ys)); setModal({ open: false, id: null }); }}
      />
    </div>
  );
}
